import { useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { theme } from 'theme';
import { getCaseDetailsThunk } from 'reducers/caseReducer';
import { closeModal } from 'reducers/modalReducer';
import { setCaseError } from 'reducers/caseReducer';
import { selectCase, selectCaseError, selectIsCaseLoading } from 'selectors/caseSelector';
import { NewAccountEnum, CustomError } from 'types';
import { AccountInformation } from 'views/Case/AccountInformation';
import { CaseStatus } from 'views/Case/CaseStatus';
import { Proposals } from 'views/Case/Proposals';
import { CaseButtonActions } from 'views/Case/CaseButtonActions';
import CustomCircularProgress from 'components/common/CustomCircularProgress';
import { useErrorHandling } from 'components/hooks/useErrorHandling';
import { useNavigate } from 'react-router';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import { selectAccountNumber } from 'selectors/accountSelector';
import { AppDispatch } from 'store';

const styles = {
  container: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(14),
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
  spinner: {
    height: 'inherit',
  },
};

type Props = {
  productId?: string;
};
export const CaseOverview = ({ productId }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const error: CustomError | undefined = useSelector(selectCaseError);
  const isLoading = useSelector(selectIsCaseLoading);
  const caseItem = useSelector(selectCase);
  const partnerNumber = useSelector(selectPartnerId);
  const accountNumber = useSelector(selectAccountNumber);

  useErrorHandling(error, () => {
    if (partnerNumber) {
      navigate(`/partner?partnerNumber=${partnerNumber}`);
    } else if (accountNumber) {
      navigate(`/accounts?accountNumber=${accountNumber}`);
    }
    dispatch(setCaseError(undefined));
    dispatch(closeModal());
  });

  useEffect(() => {
    if (productId) {
      dispatch(getCaseDetailsThunk(productId));
    }
  }, [dispatch, productId]);

  return (
    <Box data-testid="case-overview">
      {isLoading ? (
        <Grid container justifyContent={'center'} alignItems={'center'} sx={styles.spinner}>
          <Grid item>
            <CustomCircularProgress size={70} data-testid={'case-overview-circular-progress'} />
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid sx={styles.container}>
            <Typography sx={styles.title} variant="h1">
              {caseItem?.placeholderName ? caseItem?.placeholderName : 'UNNAMED OFFERING'}
            </Typography>
            <Grid container direction={'column'}>
              <CaseStatus status={caseItem?.status} />
              <AccountInformation
                account={caseItem?.accountInformation?.account || NewAccountEnum.NEW}
                portfolio={caseItem?.accountInformation?.portfolio || '-'}
                isNewBrRequired={caseItem?.accountInformation?.isRequiredNewBr || false}
              />
              <Proposals proposals={caseItem?.proposals || []} />
            </Grid>
          </Grid>
          <CaseButtonActions caseId={productId} />
        </>
      )}
    </Box>
  );
};
